import React, { useState, useEffect, useMemo } from 'react';

const CandidatesTab = () => {
    // --- STATE ---
    const [candidates, setCandidates] = useState([]);
    const [filteredCandidates, setFilteredCandidates] = useState([]);
    const [search, setSearch] = useState('');
    
    // Selection State
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Config & Content State
    const [roles, setRoles] = useState([]);
    const [applyContent, setApplyContent] = useState({});
    const [newRole, setNewRole] = useState('');
    const [msg, setMsg] = useState('');
    const [loading, setLoading] = useState(false);

    // --- INITIAL FETCH ---
    useEffect(() => {
        fetchCandidates();
        fetchRoles();
        fetchApplyContent();
    }, []);

    // Search Filtering
    useEffect(() => {
        const lowerSearch = search.toLowerCase();
        const filtered = candidates.filter(c => 
            c.full_name?.toLowerCase().includes(lowerSearch) ||
            c.mobile?.includes(search) ||
            c.current_city?.toLowerCase().includes(lowerSearch) ||
            c.roles?.some(r => r.preferred_role?.toLowerCase().includes(lowerSearch))
        );
        setFilteredCandidates(filtered);
    }, [search, candidates]);

    const fetchCandidates = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/candidates');
            const data = await res.json();
            setCandidates(data);
            setFilteredCandidates(data);
        } catch (error) {
            console.error("Error fetching candidates:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        const res = await fetch('/api/admin/config/role'); // Updated route based on previous context if needed, otherwise /api/roles
        // Assuming your route is /api/school/config or similar based on server.js. 
        // Note: In your provided code it was /api/roles but in server.js it seemed to be /api/admin/config/role or generic config.
        // Falling back to your provided code structure but robust:
        try {
           const res = await fetch('/api/school/config'); // Trying the config route from server.js
           if(res.ok) {
               const data = await res.json();
               setRoles(data);
           }
        } catch(e) { console.log(e); }
    };

    const fetchApplyContent = async () => {
        try {
            const res = await fetch('/api/apply/content');
            const data = await res.json();
            if(data) setApplyContent(data);
        } catch(e) { console.log(e); }
    };

    // --- SELECTION HANDLERS ---

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = filteredCandidates.map(c => c.id);
            setSelectedIds(new Set(allIds));
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleSelectOne = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
    };

    // --- SMART ACTION HANDLERS ---

    // 1. Export to Excel (CSV)
    const handleExportExcel = () => {
        // Determine which data to export: Selected OR All Filtered
        const dataToExport = selectedIds.size > 0 
            ? candidates.filter(c => selectedIds.has(c.id))
            : filteredCandidates;

        if (dataToExport.length === 0) return alert("No data to export");

        // Define CSV Headers
        const headers = [
            "Applied Date", "Full Name", "Mobile", "Current State", "Current District", "Current City",
            "Experience", "Expected Salary", "Pref. Joining Date", "Pref. Locations",
            "Roles (Role | Level | Subject)"
        ];

        // Map Data to CSV Rows
        const rows = dataToExport.map(c => {
            // Format Roles into a single string
            const roleString = c.roles && c.roles.length > 0 
                ? c.roles.map(r => {
                    const parts = [r.preferred_role || r.custom_role];
                    if (r.level) parts.push(r.level);
                    if (r.subject) parts.push(r.subject);
                    return parts.join(' - ');
                }).join('; ')
                : "No Role Specified";

            return [
                `"${new Date(c.applied_at).toLocaleDateString()}"`,
                `"${c.full_name}"`,
                `"${c.mobile}"`,
                `"${c.current_state || ''}"`,
                `"${c.current_district || ''}"`,
                `"${c.current_city || ''}"`,
                `"${c.experience || ''}"`,
                `"${c.expected_salary || ''}"`,
                `"${c.preferred_joining_date ? new Date(c.preferred_joining_date).toLocaleDateString() : ''}"`,
                `"${c.preferred_city || ''}"`,
                `"${roleString}"`
            ].join(",");
        });

        // Combine and Download
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Candidates_Export_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 2. Smart CV Download
    const handleSmartDownloadCV = async () => {
        const dataToDownload = selectedIds.size > 0 
            ? candidates.filter(c => selectedIds.has(c.id))
            : filteredCandidates;

        if (dataToDownload.length === 0) return alert("No candidates found to download CVs.");

        // Extract CV paths
        const filePaths = dataToDownload.map(c => c.cv_path).filter(path => path);

        if (filePaths.length === 0) return alert("Selected candidates do not have CV files attached.");

        setLoading(true);
        try {
            const response = await fetch('/api/candidates/download-selected-cvs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filePaths })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Selected_CVs_${Date.now()}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                alert("Error downloading CVs. Some files might be missing on server.");
            }
        } catch (error) {
            console.error(error);
            alert("Network error while downloading.");
        } finally {
            setLoading(false);
        }
    };

    // --- OTHER HANDLERS (Role & Content) ---
    // (Keeping logic simple for roles as per request, but note that server uses config table now)
    const addRole = async () => {
        // Note: This matches your previous logic, but ensure server API matches
        if (!newRole.trim()) return;
        alert("Please use the 'Form Config' tab to add detailed roles properly.");
        setNewRole('');
    };

    const deleteRole = async (id) => {
        // Placeholder for deletion
        if(!window.confirm("Delete this role?")) return;
        // Call API if needed
        const newRoles = roles.filter(r => r.id !== id);
        setRoles(newRoles);
    };

    const saveApplyContent = async (e) => {
        e.preventDefault();
        const res = await fetch('/api/apply/content', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(applyContent)
        });
        if(res.ok) setMsg('Content updated!');
    };

    return (
        <div className="space-y-8 animate-fade-in">
            
            {/* --- SECTION 1: SMART CANDIDATES TABLE --- */}
            <div className="bg-white p-6 rounded-2xl shadow-md border border-slate-100">
                
                {/* Header & Controls */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                    <div>
                        <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Candidate Applications</h3>
                        <p className="text-sm text-slate-400 font-medium mt-1">
                            Showing {filteredCandidates.length} of {candidates.length} candidates
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
                        {/* Search Bar */}
                        <div className="relative flex-grow md:flex-grow-0">
                            <input 
                                type="text" 
                                placeholder="Search by name, city, role..." 
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
                            />
                            <svg className="w-4 h-4 text-slate-400 absolute left-3 top-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                            <button 
                                onClick={handleExportExcel}
                                className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-bold text-xs uppercase hover:bg-emerald-700 shadow-md transition-all flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                                {selectedIds.size > 0 ? `Export (${selectedIds.size})` : 'Export All'}
                            </button>

                            <button 
                                onClick={handleSmartDownloadCV}
                                disabled={loading}
                                className={`px-4 py-2 rounded-lg font-bold text-xs uppercase shadow-md transition-all flex items-center gap-2 ${loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                            >
                                {loading ? (
                                    <span>Processing...</span>
                                ) : (
                                    <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                                    {selectedIds.size > 0 ? `Download CVs (${selectedIds.size})` : 'Download All CVs'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto border rounded-xl">
                    <table className="w-full text-sm text-left border-collapse">
                        <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-xs">
                            <tr>
                                <th className="px-4 py-3 border-b text-center w-10">
                                    <input 
                                        type="checkbox" 
                                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                        checked={filteredCandidates.length > 0 && selectedIds.size === filteredCandidates.length}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th className="px-4 py-3 border-b min-w-[150px]">Candidate Info</th>
                                <th className="px-4 py-3 border-b min-w-[120px]">Location</th>
                                <th className="px-4 py-3 border-b min-w-[200px]">Roles Applied</th>
                                <th className="px-4 py-3 border-b">Exp / Salary</th>
                                <th className="px-4 py-3 border-b">Joining Date</th>
                                <th className="px-4 py-3 border-b text-center">CV</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredCandidates.map((c) => (
                                <tr key={c.id} className={`hover:bg-blue-50 transition-colors ${selectedIds.has(c.id) ? 'bg-blue-50' : ''}`}>
                                    <td className="px-4 py-3 text-center">
                                        <input 
                                            type="checkbox" 
                                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                            checked={selectedIds.has(c.id)}
                                            onChange={() => handleSelectOne(c.id)}
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="font-bold text-slate-800 text-base">{c.full_name}</div>
                                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                            {c.mobile}
                                        </div>
                                        <div className="text-[10px] text-slate-400 mt-1">Applied: {new Date(c.applied_at).toLocaleDateString()}</div>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">
                                        <div className="font-semibold">{c.current_city}</div>
                                        <div className="text-xs text-slate-400">{c.current_district}, {c.current_state}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-1">
                                            {c.roles && c.roles.length > 0 ? (
                                                c.roles.map((r, idx) => (
                                                    <span key={idx} className="bg-orange-50 text-orange-700 px-2 py-1 rounded border border-orange-100 text-xs font-semibold inline-block">
                                                        {r.preferred_role || r.custom_role} 
                                                        {(r.level || r.subject) && <span className="text-orange-400 font-normal"> — {r.level} {r.subject ? `(${r.subject})` : ''}</span>}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-slate-400 text-xs italic">No specific roles</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-xs font-bold text-slate-700">Exp: {c.experience}</div>
                                        <div className="text-xs text-slate-500 mt-1">Exp. Salary: {c.expected_salary}</div>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600 text-xs font-medium">
                                        {c.preferred_joining_date ? new Date(c.preferred_joining_date).toLocaleDateString() : 'Immediate'}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <a 
                                            href={`${c.cv_path}`} 
                                            target="_blank" 
                                            rel="noreferrer" 
                                            className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded text-xs font-bold transition-colors inline-flex items-center gap-1"
                                        >
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                            View
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredCandidates.length === 0 && (
                        <div className="text-center py-10 bg-slate-50">
                            <p className="text-slate-400 font-medium">No candidates found matching your search.</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* SECTION 2: ROLES DISPLAY (Visual Only - Use Config Tab for Edit) */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 opacity-70 hover:opacity-100 transition-opacity">
                    <h3 className="text-lg font-black text-slate-800 uppercase mb-2">Available Roles (Preview)</h3>
                    <p className="text-xs text-slate-400 mb-4">To edit roles, please use the <strong>Form Config</strong> tab.</p>
                    
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                        {roles.map((role, idx) => (
                            <span key={idx} className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-bold border border-slate-200">
                                {role.role_name}
                            </span>
                        ))}
                    </div>
                </div>

                {/* SECTION 3: APPLY CONTENT EDIT */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <h3 className="text-lg font-black text-slate-800 uppercase mb-4">Update 'Apply Now' Text</h3>
                    <form onSubmit={saveApplyContent} className="space-y-3">
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase">Badge Text</label>
                            <input value={applyContent.badge_text || ''} onChange={(e) => setApplyContent({...applyContent, badge_text: e.target.value})} className="w-full p-2 border rounded text-xs focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-slate-400 uppercase">Main Heading</label>
                                <input value={applyContent.main_heading || ''} onChange={(e) => setApplyContent({...applyContent, main_heading: e.target.value})} className="w-full p-2 border rounded font-bold focus:ring-2 focus:ring-blue-500 outline-none" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-orange-400 uppercase">Highlight Text</label>
                                <input value={applyContent.highlight_heading || ''} onChange={(e) => setApplyContent({...applyContent, highlight_heading: e.target.value})} className="w-full p-2 border rounded font-bold text-orange-500 focus:ring-2 focus:ring-orange-500 outline-none" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase">Sub Heading</label>
                            <input value={applyContent.sub_heading || ''} onChange={(e) => setApplyContent({...applyContent, sub_heading: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase">Description</label>
                            <textarea value={applyContent.description || ''} onChange={(e) => setApplyContent({...applyContent, description: e.target.value})} className="w-full p-2 border rounded h-20 text-xs focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                        <button type="submit" className="bg-slate-900 text-white px-4 py-2 rounded-lg font-bold uppercase text-xs w-full hover:bg-black transition-colors">Update Content</button>
                        {msg && <p className="text-center text-green-500 text-xs font-bold mt-2 bg-green-50 p-1 rounded">{msg}</p>}
                    </form>
                </div>

            </div>
        </div>
    );
};

export default CandidatesTab;